import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { asRef, dayjs } from '@/lib/format';
import type { TimeEntry } from '@/lib/types';
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { useAuthStore } from '@/stores/authStore';
import { useUiStore } from '@/stores/uiStore';
import { useMarkNotificationsRead, useNotifications, useRunningTimer, useStopTimer } from '@/lib/queries';
import { UserAvatar } from '@/components/common/UserAvatar';
import { formatRelative } from '@/lib/format';

export function TopBar() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { mode, toggleMode, setCommandPalette } = useUiStore();

  const { data: notifications } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const { data: timer } = useRunningTimer();
  const stopTimer = useStopTimer();

  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const [bellAnchor, setBellAnchor] = useState<HTMLElement | null>(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        height: 60,
        px: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: (t) => alpha(t.palette.background.paper, 0.85),
        backdropFilter: 'blur(10px)',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Button
        onClick={() => setCommandPalette(true)}
        startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
        sx={{
          justifyContent: 'flex-start',
          minWidth: { xs: 44, sm: 300 },
          color: 'text.secondary',
          bgcolor: 'surface.sunken',
          border: 1,
          borderColor: 'divider',
          fontWeight: 500,
          '&:hover': { bgcolor: 'surface.sunken', borderColor: 'primary.main' },
        }}
      >
        <Box sx={{ display: { xs: 'none', sm: 'block' }, flex: 1, textAlign: 'left' }}>
          Search everything…
        </Box>
        <Chip
          size="small"
          label="⌘K"
          sx={{ display: { xs: 'none', sm: 'flex' }, height: 20, fontSize: '0.6875rem' }}
        />
      </Button>

      <Box sx={{ flex: 1 }} />

      {timer && <RunningTimerChip timer={timer} onStop={() => stopTimer.mutate()} />}

      <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
        <IconButton onClick={toggleMode} size="small">
          {mode === 'dark' ? (
            <LightModeOutlinedIcon fontSize="small" />
          ) : (
            <DarkModeOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title="Notifications">
        <IconButton size="small" onClick={(e) => setBellAnchor(e.currentTarget)}>
          <Badge badgeContent={notifications?.unreadCount ?? 0} color="error" max={99}>
            <NotificationsNoneIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      {user && (
        <IconButton size="small" onClick={(e) => setProfileAnchor(e.currentTarget)}>
          <UserAvatar
            user={{
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              avatarColor: user.avatarColor,
            }}
            size={30}
            showTooltip={false}
          />
        </IconButton>
      )}

      <Menu
        anchorEl={profileAnchor}
        open={!!profileAnchor}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 2.5, mt: 1 } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 650 }}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setProfileAnchor(null);
            navigate('/settings');
          }}
        >
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          Profile & settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      <Popover
        anchorEl={bellAnchor}
        open={!!bellAnchor}
        onClose={() => setBellAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, borderRadius: 2.5, mt: 1 } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography variant="h6">Notifications</Typography>
          <Button size="small" onClick={() => markRead.mutate(undefined)}>
            Mark all read
          </Button>
        </Stack>

        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {!notifications?.items.length && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              You're all caught up.
            </Typography>
          )}
          {notifications?.items.map((item) => (
            <Box
              key={item.id}
              onClick={() => {
                markRead.mutate([item.id]);
                if (item.link) navigate(item.link);
                setBellAnchor(null);
              }}
              sx={{
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: item.isRead
                  ? 'transparent'
                  : (t) => alpha(t.palette.primary.main, 0.05),
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: item.isRead ? 500 : 650 }}>
                {item.title}
              </Typography>
              {item.body && (
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {item.body}
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled">
                {formatRelative(item.createdAt)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}


/**
 * The running timer, in the header.
 *
 * It ticks. A static chip answers "is something running" but not "for how
 * long", which is the question that actually matters when you find a timer you
 * forgot at 6pm — and the elapsed figure is the difference between noticing
 * that and not.
 *
 * The tooltip carries what the chip has no room for: the task, its project,
 * and when the timer started. Hover rather than click, because the chip's
 * click target is the stop button and a menu would put "discard my timing" one
 * mis-click away.
 */
function RunningTimerChip({ timer, onStop }: { timer: TimeEntry; onStop: () => void }) {
  const startedAt = timer.startedAt ? dayjs(timer.startedAt) : null;

  // Re-rendered every second only while a timer exists; the interval is torn
  // down with the chip.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const task = asRef(timer.taskId);
  const project = asRef(timer.projectId);
  const seconds = startedAt ? Math.max(0, dayjs().diff(startedAt, 'second')) : 0;
  const elapsed = [
    Math.floor(seconds / 3600),
    Math.floor((seconds % 3600) / 60),
    seconds % 60,
  ]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');

  return (
    <Tooltip
      title={
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {task ? `${task.key} · ${task.title}` : 'Timer running'}
          </Typography>
          {project && (
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
              {project.name}
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            Started {startedAt ? startedAt.format('h:mm A') : 'recently'} · running {elapsed}
          </Typography>
          {timer.description && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
              {timer.description}
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.7 }}>
            Stop to log the time — nothing is saved until you do.
          </Typography>
        </Box>
      }
    >
      <Chip
        color="warning"
        variant="outlined"
        onDelete={onStop}
        deleteIcon={<StopCircleOutlinedIcon />}
        label={
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: 'warning.main',
                // Pulses so a forgotten running timer is hard to miss.
                animation: 'pulse 1.6s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.25 },
                },
              }}
            />
            <span>{task ? task.key : 'Timer'}</span>
            {/* Tabular figures so the width does not jitter every second. */}
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
              {elapsed}
            </Box>
          </Stack>
        }
      />
    </Tooltip>
  );
}
